import { diff } from "deep-object-diff";

import {
  createDefaultBillingPaymentSchedule,
  defaultBillingLocationIncreaseComparison,
  defaultBillingWeighted,
} from "../../consts/billing";
import {
  BillingApiErrors,
  BillingPaymentScheduleType,
  BillingStatus,
  IBilling,
  IBillingApi,
  IBillingPayment,
  IBillingPaymentApi,
  IBillingPaymentSchedule,
  IBillingPaymentScheduleApi,
  IBillingPut,
  IBillingRefund,
  IBillingRefundApi,
} from "../../types/billings";
import { toDateString } from "../../utils/date";
import { generateRandomId } from "../../utils/id";

const adaptWeightedBillingForFrontend = (weightedBilling?: IBillingApi["WeightedBillings"]): IBilling["weighted"] => {
  if (!weightedBilling) {
    return structuredClone(defaultBillingWeighted);
  }

  return {
    averageContainerWeightPerCollection: weightedBilling.average_container_weight_per_collection ?? 0,
    plannedAnnualCollectionCount: weightedBilling.planned_annual_collection_count ?? 0,
    advanceContainerBasis: weightedBilling.advance_container_basis ?? 0,
    rate: Number(weightedBilling.rate),
    flatFee: Number(weightedBilling.flat_fee),
    avgPublicWastePerPerson: weightedBilling.avg_public_waste_per_person,
    suggestedFlatFee: Number(weightedBilling.suggested_flat_fee),
    suggestedRate: Number(weightedBilling.suggested_rate),
    lastYearsWasteSum: weightedBilling.last_years_waste_sum,
    minWastePerHousehold: weightedBilling.min_waste_per_household,
    medianWastePerHousehold: weightedBilling.median_waste_per_household,
  };
};

const adaptBillingExpensesForFrontend = (billingExpenses?: IBillingApi["BillingExpenses"]): IBilling["expenses"] => {
  if (!billingExpenses) {
    return [];
  }
  return billingExpenses.map((expense) => ({
    id: generateRandomId(),
    apiId: expense.id,
    name: expense.name,
    value: String(expense.value),
    real: expense.real,
  }));
};

export const adaptBillingLocationIncreaseComparisonForFrontend = (
  locationIncreaseComparison?: IBillingApi["LocationBillingIncreaseComparison"]
): IBilling["locationIncreaseComparison"] => {
  if (!locationIncreaseComparison) {
    return structuredClone(defaultBillingLocationIncreaseComparison);
  }
  return {
    negative25: locationIncreaseComparison.negative_25,
    negative20To25: locationIncreaseComparison.negative_20_to_25,
    negative15To20: locationIncreaseComparison.negative_15_to_20,
    negative10To15: locationIncreaseComparison.negative_10_to_15,
    negative5To10: locationIncreaseComparison.negative_5_to_10,
    negative0To5: locationIncreaseComparison.negative_0_to_5,
    positive0To5: locationIncreaseComparison.positive_0_to_5,
    positive5To10: locationIncreaseComparison.positive_5_to_10,
    positive10To15: locationIncreaseComparison.positive_10_to_15,
    positive15To20: locationIncreaseComparison.positive_15_to_20,
    positive20To25: locationIncreaseComparison.positive_20_to_25,
    positive25: locationIncreaseComparison.positive_25,
  };
};

export const adaptBillingPaymentScheduleForFrontend = (
  year: number,
  paymentSchedule?: IBillingPaymentScheduleApi | null
): IBillingPaymentSchedule => {
  const defaultPaymentSchedule = createDefaultBillingPaymentSchedule(year);

  if (!paymentSchedule) {
    return defaultPaymentSchedule;
  }

  const schedule = paymentSchedule;

  return {
    annualDueDate: schedule.annual_due_date || defaultPaymentSchedule.annualDueDate,
    q1DueDate: schedule.q1_due_date || defaultPaymentSchedule.q1DueDate,
    q2DueDate: schedule.q2_due_date || defaultPaymentSchedule.q2DueDate,
    q3DueDate: schedule.q3_due_date || defaultPaymentSchedule.q3DueDate,
    q4DueDate: schedule.q4_due_date || defaultPaymentSchedule.q4DueDate,
    type: schedule.type,
  };
};

export const adaptBillingPaymentsForFrontend = (payments: IBillingPaymentApi[] | null): IBillingPayment[] => {
  if (!payments) {
    return [];
  }

  return payments
    .map((payment) => ({
      amount: Number(payment.amount),
      paymentDate: payment.payment_date,
      period: payment.period,
    }))
    .sort((a, b) => a.period.localeCompare(b.period));
};

export const adaptBillingRefundsForFrontend = (refunds: IBillingRefundApi[] | null): IBillingRefund[] => {
  if (!refunds) {
    return [];
  }

  return refunds.map((refund) => ({
    id: refund.id,
    amount: Number(refund.amount),
    paymentDate: refund.payment_date,
    reason: refund.reason,
  }));
};

export const adaptBillingResponseForFrontend = (apiItem: IBillingApi): IBilling => {
  const expenses = adaptBillingExpensesForFrontend(apiItem?.BillingExpenses);
  const weighted = adaptWeightedBillingForFrontend(apiItem?.WeightedBillings);

  return {
    billingId: apiItem.id,
    year: apiItem.year,
    isZeroYear: apiItem.is_zero_year === true,
    expectedWasteAmount: String(apiItem.expected_waste_amount),
    expectedExpenseSummary: apiItem.expected_expense_summary,
    wasteAmount: String(apiItem.waste_amount),
    expenseSummary: apiItem.expense_summary,
    submitted: apiItem.submitted,
    balance: Number(apiItem.balance),
    status: apiItem.status,
    totalFeeSum: Number(apiItem.total_fee_sum),
    totalNetFeeSum: Number(apiItem.total_net_fee_sum),
    householdMemberSum: apiItem.household_member_sum,
    totalCollectedFeeSum: Number(apiItem.total_collected_fee_sum),
    avgHouseholdFee: Number(apiItem.avg_household_fee),
    avgResidentFee: Number(apiItem.avg_resident_fee),
    avgHouseholdFeeIncrease: apiItem.avg_household_fee_increase ? Number(apiItem.avg_household_fee_increase) : null,
    avgResidentFeeIncrease: apiItem.avg_resident_fee_increase ? Number(apiItem.avg_resident_fee_increase) : null,
    householdCount: apiItem.household_count,
    householdWithFixedOnlyCount: apiItem.household_with_fixed_only_count,
    expenses,
    removeExpenseApiIds: [],
    weighted,
    locationIncreaseComparison: adaptBillingLocationIncreaseComparisonForFrontend(
      apiItem?.LocationBillingIncreaseComparison
    ),
    paymentSchedule: adaptBillingPaymentScheduleForFrontend(apiItem.year, apiItem?.PaymentSchedules?.[0]),
    hasBeenSaved: !!apiItem.LocationBillingIncreaseComparison,
  };
};

export const adaptBillingsResponseForFrontend = (apiList: IBillingApi[]): IBilling[] => {
  return apiList.map((apiListItem) => adaptBillingResponseForFrontend(apiListItem));
};

const adaptWeightedBillingPutForBackend = (item: IBilling): NonNullable<IBillingPut["weighted_rates"]> => ({
  ...(item.isZeroYear && {
    average_container_weight_per_collection: Number(item.weighted.averageContainerWeightPerCollection),
    planned_annual_collection_count: Number(item.weighted.plannedAnnualCollectionCount),
  }),
  rate: Number(item.weighted.rate),
  flat_fee: Number(item.weighted.flatFee),
  min_waste_per_household: Number(item.weighted.minWastePerHousehold),
});

export const adaptBillingPutForBackend = (item: IBilling, originalItem: IBilling): IBillingPut | null => {
  const itemDiff = diff(originalItem, item) as Partial<IBilling>;

  if (Object.keys(itemDiff).length === 0) {
    return item.isZeroYear && item.status === BillingStatus.New
      ? { id: item.billingId, weighted_rates: adaptWeightedBillingPutForBackend(item) }
      : null;
  }

  const putBilling: IBillingPut = {
    id: item.billingId,
  };

  if (Object.prototype.hasOwnProperty.call(itemDiff, "wasteAmount")) {
    putBilling.waste_amount = Number(item.wasteAmount);
  }

  if (Object.prototype.hasOwnProperty.call(itemDiff, "expectedWasteAmount")) {
    putBilling.expected_waste_amount = Number(item.expectedWasteAmount);
  }

  if (Object.prototype.hasOwnProperty.call(itemDiff, "expenses")) {
    const expenses: IBillingPut["expenses"] = item.expenses
      .filter((expense) => {
        if (item.status === BillingStatus.New && !expense.real) {
          return true;
        } else if (item.status === BillingStatus.Active && expense.real) {
          return true;
        }
        return false;
      })
      .map((expense) => ({
        id: expense.apiId,
        name: expense.name,
        value: Number(expense.value),
        real: expense.real,
      }));

    const removedExpenses: IBillingPut["expenses"] = item.removeExpenseApiIds.map((eventId) => ({
      id: eventId,
    }));

    putBilling.expenses = [...expenses, ...removedExpenses];
  }

  if (Object.prototype.hasOwnProperty.call(itemDiff, "weighted")) {
    putBilling.weighted_rates = adaptWeightedBillingPutForBackend(item);
  }

  if (Object.prototype.hasOwnProperty.call(itemDiff, "paymentSchedule")) {
    if (item.paymentSchedule.type === BillingPaymentScheduleType.Annual) {
      putBilling.payment_schedule = {
        annual_due_date: toDateString(item.paymentSchedule.annualDueDate),
      };
    } else {
      putBilling.payment_schedule = {
        q1_due_date: toDateString(item.paymentSchedule.q1DueDate),
        q2_due_date: toDateString(item.paymentSchedule.q2DueDate),
        q3_due_date: toDateString(item.paymentSchedule.q3DueDate),
        q4_due_date: toDateString(item.paymentSchedule.q4DueDate),
      };
    }
  }

  return putBilling;
};

export const adaptBillingsErrorResponseForFrontend = (errorMsg?: string | null): BillingApiErrors => {
  switch (errorMsg) {
    case "Billing does not exist.":
      return BillingApiErrors.NotFound;
    case "Billing is closed.":
      return BillingApiErrors.Closed;
    case "Billing cannot be updated.":
      return BillingApiErrors.CannotUpdate;
    case "Cannot update expected waste amount on active billing.":
      return BillingApiErrors.CannotUpdateExpectedWasteAmount;
    case "Cannot update expected expenses on active billing.":
      return BillingApiErrors.CannotUpdateExpectedExpenses;
    case "Cannot update rates on active billing.":
      return BillingApiErrors.CannotUpdateRates;
    case "An open billing already exists":
      return BillingApiErrors.AnOpenBillingExists;
    case "Billing cannot be deleted.":
      return BillingApiErrors.CannotDelete;
    case "User does not have permission to access this module":
      return BillingApiErrors.NoPermission;
    default:
      return BillingApiErrors.Default;
  }
};
